import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { CrmBadge, CrmButton, CrmTooltip } from '../components/ui';
import { CrmEntityListPage } from '../components/listing';
import NotificationSnackbar from '../components/ui/NotificationSnackbar';
import { listStorefronts } from '../services/commercialContextService';

const normalizeStorefronts = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  return [];
};

const isActiveStorefront = (storefront) => ![false, 0, '0', 'false'].includes(storefront?.is_active);
const displayDomain = (domain) => String(domain || '').trim() || '—';

const Storefronts = () => {
  const navigate = useNavigate();
  const [storefronts, setStorefronts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const loadStorefronts = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const result = await listStorefronts();
      setStorefronts(normalizeStorefronts(result).filter(isActiveStorefront));
    } catch (requestError) {
      const message = requestError?.response?.data?.error || requestError?.message || 'Failed to load storefronts.';
      setError(message);
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { loadStorefronts(); }, [loadStorefronts]);

  const rows = useMemo(
    () => [...storefronts].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    [storefronts]
  );

  const openExperience = useCallback((storefront) => {
    const key = storefront?.code || storefront?.id;
    if (!key) return;
    navigate(`/storefronts/${encodeURIComponent(String(key))}/experience`);
  }, [navigate]);

  const columns = useMemo(() => [
    {
      id: 'name',
      label: 'Storefront',
      width: 260,
      minWidth: 180,
      maxWidth: 420,
      hideable: false,
      defaultPinned: true,
      accessor: (row) => row.name || '',
      renderCell: (row) => (
        <button type="button" className="crm-table-primary-link" onClick={() => openExperience(row)}>
          {row.name || 'Unnamed storefront'}
        </button>
      ),
    },
    { id: 'code', label: 'Code', width: 180, minWidth: 130, maxWidth: 260, accessor: (row) => row.code || '' },
    { id: 'domain', label: 'Domain', width: 260, minWidth: 180, maxWidth: 420, accessor: (row) => displayDomain(row.domain) },
    {
      id: 'status',
      label: 'Status',
      width: 120,
      minWidth: 100,
      maxWidth: 150,
      accessor: () => 'Active',
      renderCell: () => <CrmBadge tone="success">Active</CrmBadge>,
    },
    {
      id: 'row_actions',
      label: 'Actions',
      width: 112,
      minWidth: 112,
      maxWidth: 130,
      hideable: false,
      sortable: false,
      searchable: false,
      stopRowClick: true,
      renderCell: (row) => (
        <span className="crm-row-action-group">
          <CrmTooltip content="Open storefront">
            <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => openExperience(row)} aria-label="Open storefront">
              <VisibilityOutlinedIcon />
            </CrmButton>
          </CrmTooltip>
          <CrmTooltip content="Business structure settings">
            <CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => navigate('/settings?section=structure&entity=storefronts')} aria-label="Storefront settings">
              <SettingsOutlinedIcon />
            </CrmButton>
          </CrmTooltip>
        </span>
      ),
    },
  ], [navigate, openExperience]);

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="storefronts"
        title="Storefronts"
        itemLabel="storefront"
        items={rows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={loadStorefronts}
        onRefresh={loadStorefronts}
        onSettings={() => navigate('/settings?section=structure&entity=storefronts')}
        onRowClick={openExperience}
        emptyTitle="No storefronts found"
        emptyMessage="Create or reactivate a storefront from Business Structure in Settings."
      />
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification((current) => ({ ...current, open: false }))}
      />
    </div>
  );
};

export default Storefronts;
